import React, {KeyboardEventHandler, Suspense, use, useEffect, useId, useRef, useState, useTransition} from "react";
import {RatingProfileId, SearchState, TagsSearchResult} from "../models/models";
import {
    AutocompleteSuggestions,
    SearchQueryTag,
    SearchQueryTagList,
    SearchQueryTagSuggestion,
    SearchQueryTagSuggestionList
} from "../models/search_query";
import {
    Skeleton,
    ScrollArea,
    Badge,
    View,
    Text,
    Autocomplete,
    AutocompleteProps,
    FormControl,
    Divider,
    Hotkey,
    Grid,
    Actionable,
    Alert,
    Icon,
    AutocompleteInstance
} from "reshaped";
import {
    AlertTriangleIcon,
    SearchIcon,
} from "lucide-react";
import {ErrorBoundary} from "react-error-boundary";
import {loadPlaceSuggestions, tagsSearch, loadTagSuggestions} from "../utils/api_client";
import {EntryTile, EntryTileLoading} from "./entry_tile";
import {SearchTagIcon} from "./badges";
import {useTranslation} from "react-i18next";
import {suggestionFromSearchQueryTagValue, urlWithRatingProfileIdParam} from "../utils/helpers";
import {useGeoPosition} from "../utils/geo_position_provider";


interface SearchSidebarProps {
    searchState: SearchState;
    onSelectedQueryTagsChange: (queryTags: SearchQueryTagList) => void;
}

export default function SearchSidebar({searchState, onSelectedQueryTagsChange}: SearchSidebarProps) {
    const searchMaskTitleId = useId();
    const { t } = useTranslation();

    return (
        <div className={"h-full"}>
            <h2 className="sr-only">{t("search_sidebar.search_sidebar.sidebar_title")}</h2>

            <Grid rows={"auto 1fr"}
                  className="h-full print:shadow-none">
                <Grid.Item className={"min-w-0"}>
                    <search role={"search"} aria-labelledby={searchMaskTitleId}>
                        <h3 id={searchMaskTitleId} className="sr-only">{t("search_sidebar.search_sidebar.field_title")}</h3>
                        <SearchField searchState={searchState} onSelectedQueryTagsChange={onSelectedQueryTagsChange} />
                    </search>
                </Grid.Item>
                <Grid.Item className={"min-h-0"}>
                    <h3 className="sr-only">{t("search_sidebar.search_sidebar.results_title")}</h3>

                    <SearchResultListWithSuspense searchState={searchState}/>
                </Grid.Item>
            </Grid>
        </div>
    );
};

function SearchResultListWithSuspense({searchState} : {searchState: SearchState}) {
    const promise = tagsSearch(searchState);
    const { ratingProfileId } = searchState;

    return (
        <ErrorBoundary resetKeys={[searchState.searchQueryTags]} fallback={<ErrorMessage/>}>
            <Suspense fallback={<SearchResultListLoading/>}>
                {searchState.mapInfo ? (<SearchResultList searchResultPromise={promise} ratingProfileId={ratingProfileId} />) : <SearchResultListLoading/>}
            </Suspense>
        </ErrorBoundary>
    );
}


interface SearchFieldProps {
    searchState: SearchState;
    onSelectedQueryTagsChange: (queryTags: SearchQueryTagList) => void;
}


let tagSuggestionsPromise: Promise<AutocompleteSuggestions>;
let placeSuggestionsPromise: Promise<AutocompleteSuggestions> | null = null;

function SearchField({searchState, onSelectedQueryTagsChange}: SearchFieldProps) {
    const { t } = useTranslation();
    const autocompleteRef = useRef<AutocompleteInstance>(null);
    const [isPending, startTransition] = useTransition();
    const [value, setValue] = useState("");
    const [errorResetKey, setErrorResetKey] = useState(0);
    const [tagSuggestions, setTagSuggestions] = useState<AutocompleteSuggestions | null>(null);

    useEffect(() => {
        tagSuggestionsPromise = loadTagSuggestions();
        startTransition(async () => {
            try {
                const result = await tagSuggestionsPromise;
                startTransition(() => {
                    setTagSuggestions(result);
                })
            } catch {
                startTransition(() => {
                    setTagSuggestions(null);
                })
            }
        });
    }, [])

    const { searchQueryTags } = searchState;
    const hasPlaceTag = searchQueryTags.some(tag => tag.type === "place");

    const handleOpen = () => {
        tagSuggestionsPromise = loadTagSuggestions();
        setErrorResetKey(prev => prev + 1);
    }

    const handleInput: AutocompleteProps["onInput"] = (args) => {
        setValue(args.value);
        if (!hasPlaceTag) {
            placeSuggestionsPromise = loadPlaceSuggestions(args.value, searchState);
        } else {
            placeSuggestionsPromise = null;
        }
    }

    const handleItemSelect: AutocompleteProps["onItemSelect"] = (args) => {
        const selectedSuggestion = args.data as SearchQueryTagSuggestion;
        addSearchQueryTag(selectedSuggestion);
    };

    const handleBackspace = () => {
        if (value.length > 0 || searchQueryTags.length === 0) {
            return;
        }

        const lastTag = searchQueryTags[searchQueryTags.length - 1];
        removeQueryTag(lastTag);
    };

    const handleKeyUpOnInput: KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === "Enter" && value.length > 0) {
            addSearchQueryTag({ value: value, type: "text", text: value });
            autocompleteRef.current?.close();
        }
    };

    function removeQueryTag(tag: SearchQueryTag) {
        const newTags = searchQueryTags.filter(t => !(t.type === tag.type && t.value === tag.value));
        onSelectedQueryTagsChange(newTags);
    }

    function addSearchQueryTag(suggestion: SearchQueryTagSuggestion) {
        const newTag: SearchQueryTag = { value: suggestion.value, type: suggestion.type };
        const newTags = [...searchQueryTags, newTag];
        setValue("");
        if (newTag.type === "place") {
            placeSuggestionsPromise = null;
        }
        onSelectedQueryTagsChange(newTags);
    }

    // rebuild list from loaded SearchQueryTagSuggestions
    const allSuggestions = tagSuggestions?.groups.flatMap(group => group.queryTags);
    const badges = searchQueryTags
        .map((tag) => suggestionFromSearchQueryTagValue(tag, allSuggestions))
        .map((suggestion) => {
            return suggestion &&
                (<Badge onDismiss={() => removeQueryTag(suggestion)}
                        dismissAriaLabel={t("search_sidebar.search_field.remove_search_tag")}
                        icon={<SearchTagIcon tagType={suggestion.type} value={suggestion.value}/>}
                        color="primary"
                        variant="faded" className={"max-w-80"}>
                    <Text maxLines={1}>
                        {suggestion.text}
                    </Text>
                </Badge>);
        }
    )

    return (
        <View padding={4} className={"print:hidden"}>
            <FormControl>
                <Autocomplete
                    name="search"
                    instanceRef={autocompleteRef}
                    inputAttributes={{
                        'aria-label': t("search_sidebar.search_field.label"),
                        'onKeyUp': handleKeyUpOnInput}}
                    placeholder={t("search_sidebar.search_field.placeholder")}
                    icon={<SearchIcon/>}
                    value={value}
                    onItemSelect={handleItemSelect}
                    onInput={handleInput}
                    onBackspace={handleBackspace}
                    onOpen={handleOpen}
                    startSlot={badges}
                    multiline
                >
                    <ErrorBoundary resetKeys={[errorResetKey]} fallback={<ErrorMessage/>}>
                        <Suspense fallback={<AutocompleteListLoading/>}>
                            <AutocompleteList inputValue={value}
                                              selectedQueryTags={searchQueryTags}
                                              tagSuggestionsPromise={tagSuggestionsPromise}
                                              placeSuggestionsPromise={placeSuggestionsPromise}/>
                        </Suspense>
                    </ErrorBoundary>
                </Autocomplete>
            </FormControl>
        </View>
    );
}


interface AutocompleteListProps {
    inputValue: string;
    selectedQueryTags: Array<SearchQueryTag>;
    tagSuggestionsPromise: Promise<AutocompleteSuggestions>;
    placeSuggestionsPromise: Promise<AutocompleteSuggestions> | null;
}

function AutocompleteList({
                              inputValue,
                              selectedQueryTags,
                              tagSuggestionsPromise,
                              placeSuggestionsPromise
                          }: AutocompleteListProps) {
    const { t } = useTranslation();
    const tagSuggestionsResponse = use(tagSuggestionsPromise)
    const {groups} = tagSuggestionsResponse;

    const optionsFilter = (option: SearchQueryTagSuggestion) => {
        const valueMatch = inputValue.toLowerCase();
        const optionValue = option.text.toLowerCase();

        const matchesText = optionValue.includes(valueMatch);
        const matchesTag = selectedQueryTags.some(tag => tag.type == option.type && tag.value == option.value);
        return matchesText && !matchesTag;
    }

    const placeTag = selectedQueryTags.find(tag => tag.type == "place");
    const tagGroupItems = groups.map((tagGroup) => {
        const numFavorites = tagGroup.queryTags.filter(tag => tag.isPromoted ?? false).length;
        const filteredTags = tagGroup.queryTags.filter(optionsFilter);

        return filteredTags.length > 0 && (
            <div key={tagGroup.name}>
                <View paddingTop={2} paddingBottom={1} paddingInline={3}>
                    <Text variant="caption-1" color="neutral-faded" weight="regular">{tagGroup.name}</Text>
                </View>
                <ExtendableList list={filteredTags} visibleCount={numFavorites}/>
                <Divider/>
            </div>);
    });

    const textTag: SearchQueryTagSuggestion | null = inputValue.length > 0 ? {
        value: inputValue,
        text: t("search_sidebar.autocomplete_list.text_query", { queryText: inputValue }),
        type: "text"
    } : null;

    return (
        <Grid rows={"minmax(auto, 70vh) auto"}>
            <Grid.Item>
                <ScrollArea scrollbarDisplay="hover">
                    {!placeTag && (
                        <ErrorBoundary key={inputValue} resetKeys={[inputValue, selectedQueryTags]} fallback={<ErrorMessage/>}>
                            <Suspense fallback={<TagGroupLoading numQueryTags={4}/>}>
                                {placeSuggestionsPromise
                                    ? (<PlaceSuggestionsList placeSuggestionsPromise={placeSuggestionsPromise}/>)
                                    : (<div key={"places_hint"}>
                                        <View paddingTop={2} paddingBottom={1} paddingInline={3}>
                                            <Text variant="caption-1" color="neutral-faded" weight="regular">{t("search_sidebar.autocomplete_list.place_group_title")}</Text>
                                        </View>
                                        <CurrentLocationSuggestion />
                                        <View paddingBlock={2} paddingInline={3} direction={"row"} gap={2}>
                                            <Icon color={"neutral"} svg={<SearchTagIcon tagType={"place"}/>} size={4} />
                                            <Text variant="body-3" color="neutral" weight="medium" className={"italic"} maxLines={1}>
                                                {t("search_sidebar.autocomplete_list.place_group_hint")}
                                            </Text>
                                        </View>
                                        <Divider/>
                                    </div>)
                                }
                            </Suspense>
                        </ErrorBoundary>
                    )}

                    {tagGroupItems}

                    {textTag && (
                        <>
                            <Autocomplete.Item key={`${textTag.type}_${textTag.value}`} icon={<SearchTagIcon tagType={textTag.type}/>} value={textTag.value}
                                               data={textTag}>
                                {textTag.text}
                            </Autocomplete.Item>
                        </>)}
                </ScrollArea>
            </Grid.Item>
            <Grid.Item>
                <Divider/>
                <View padding={2} direction={"row"} justify={"end"} gap={1}>
                    <Hotkey active={false}>↓</Hotkey>
                    <Hotkey active={false}>↑</Hotkey>
                    <Text>{t("search_sidebar.autocomplete_list.navigate")}</Text>
                    <Hotkey active={false}>↵</Hotkey>
                    <Text>{t("search_sidebar.autocomplete_list.select")}</Text>
                </View>
            </Grid.Item>
        </Grid>
    );
}


interface PlaceSuggestionsListProps {
    placeSuggestionsPromise: Promise<AutocompleteSuggestions>;
}

function PlaceSuggestionsList({placeSuggestionsPromise}: PlaceSuggestionsListProps) {
    const placeSuggestionsResponse = use(placeSuggestionsPromise)
    const { position } = useGeoPosition();
    const { groups } = placeSuggestionsResponse;

    return groups.map((tagGroup) => {
        const numFavorites = tagGroup.queryTags.filter(tag => tag.isPromoted ?? false).length;
        // only 4 places in total should be shown including the current location suggestion. If there is a
        // current location detected and there are 4 or more place suggestions, we have to reduce the number of
        // suggestions to make space for the current location suggestion so that the total number of visible places is still 4.
        const maxVisiblePlaces = position ? 3 : 4;
        const filteredTags = tagGroup.queryTags.slice(0, maxVisiblePlaces);

        return filteredTags.length > 0 && (
            <div key={tagGroup.name}>
                <View paddingTop={2} paddingBottom={1} paddingInline={3}>
                    <Text variant="caption-1" color="neutral-faded" weight="regular">{tagGroup.name}</Text>
                </View>
                <CurrentLocationSuggestion />
                <ExtendableList list={filteredTags} visibleCount={numFavorites}/>
                <Divider/>
            </div>);
    });
}

function CurrentLocationSuggestion() {
    const { t } = useTranslation();
    const { position } = useGeoPosition();

    const text = t("search_sidebar.current_location_suggestion.text");
    const currentLocationTag: SearchQueryTagSuggestion | null = position
        ? { type: "place", text: text, value: `${text}/${position.lat}/${position.lng}`, isPromoted: true }
        : null;

    return currentLocationTag && (
        <Autocomplete.Item key={`${currentLocationTag.type}_${currentLocationTag.value}`} icon={<SearchTagIcon tagType={currentLocationTag.type}/>} value={currentLocationTag.value} data={currentLocationTag}>
            <Text variant="body-3" color="neutral" weight="medium" maxLines={1}>
                {currentLocationTag.text}
            </Text>
        </Autocomplete.Item>);
}


function AutocompleteListLoading() {
    const { t } = useTranslation();

    return (
        <Grid rows={"minmax(auto, 80vh) auto"}>
            <Grid.Item>
                <ScrollArea scrollbarDisplay="hover">
                    <TagGroupLoading numQueryTags={4}/>
                    <TagGroupLoading numQueryTags={3}/>
                    <TagGroupLoading numQueryTags={3}/>
                </ScrollArea>
            </Grid.Item>
            <Grid.Item>
                <Divider/>

                <View padding={2} direction={"row"} justify={"end"} gap={1}>
                    <Hotkey active={false}>↓</Hotkey>
                    <Hotkey active={false}>↑</Hotkey>
                    <Text>{t("search_sidebar.autocomplete_list.navigate")}</Text>
                    <Hotkey active={false}>↵</Hotkey>
                    <Text>{t("search_sidebar.autocomplete_list.select")}</Text>
                </View>
            </Grid.Item>
        </Grid>
    );
}


interface TagGroupLoadingProps {
    numQueryTags: number;
}

function TagGroupLoading({numQueryTags}: TagGroupLoadingProps) {
    return (
        <>
            <View paddingBlock={2} paddingInline={3}>
                <Skeleton width={"100px"} height={4}/>
            </View>
            {Array.from({length: numQueryTags}).map((_, i) => (
                <View key={i} paddingBlock={2} paddingInline={3} direction={"row"} gap={2}>
                    <Skeleton height={5} width={5} borderRadius="circular"/>
                    <Skeleton width={"165px"} height={5}/>
                </View>
            ))}
            <Divider/>
        </>
    );
}

interface ExtendableListProps {
    list: SearchQueryTagSuggestionList;
    visibleCount: number;
}

function ExtendableList({list, visibleCount}: ExtendableListProps) {
    const { t } = useTranslation();
    const collapsibleId = useId();
    const [isCollapsed, setIsCollapsed] = useState(true);
    const favoriteItems = list.filter(st => st.isPromoted ?? false);
    const collapsedItems = list.filter(st => !(st.isPromoted ?? false));
    const allItems = favoriteItems.concat(collapsedItems);
    const hasCollapsedItems = allItems.length > visibleCount;

    const handleExpandToggleClick = () => {
        setIsCollapsed(!isCollapsed);
    }

    return (
        <>
            {allItems.slice(0, visibleCount).map((option) => (
                <Autocomplete.Item key={`${option.type}_${option.value}`} icon={<SearchTagIcon tagType={option.type} value={option.value}/>} value={option.value} data={option}>
                    <Text variant="body-3" color="neutral" weight="medium" maxLines={1}>
                        {option.text}
                    </Text>
                </Autocomplete.Item>
            ))}
            {hasCollapsedItems && (
                <>
                    <div id={collapsibleId} hidden={isCollapsed}>
                        {allItems.slice(visibleCount).map((option) => (
                            <Autocomplete.Item key={`${option.type}_${option.value}`} icon={<SearchTagIcon tagType={option.type} value={option.value}/>} value={option.value}
                                               data={option}>
                                <Text variant="body-3" color="neutral" weight="medium" maxLines={1}>
                                    {option.text}
                                </Text>
                            </Autocomplete.Item>
                        ))}
                    </div>
                    <div>
                        <Actionable attributes={{'aria-controls': collapsibleId, 'aria-expanded': !isCollapsed}}
                                    onClick={handleExpandToggleClick}>
                            <View paddingBlock={2} paddingInline={3}>
                                <Text variant="caption-1" weight="regular" color="primary">
                                    {isCollapsed ? t("search_sidebar.extendable_list.show_more", { count: collapsedItems.length }) : t("search_sidebar.extendable_list.show_less")}
                                </Text>
                            </View>
                        </Actionable>
                    </div>
                </>
            )}
        </>
    )
}


interface SearchResultListProps {
    searchResultPromise: Promise<TagsSearchResult>;
    ratingProfileId: RatingProfileId;
}

function SearchResultList({searchResultPromise, ratingProfileId}: SearchResultListProps) {
    const { t } = useTranslation();
    const searchResult = use(searchResultPromise)
    const {hitlist} = searchResult;

    const hits = hitlist.searchResults.map(item => {
        const url = urlWithRatingProfileIdParam(item.entry.webUrl, ratingProfileId);
        return (
            <li key={item.entry.id}>
                <Actionable href={url.href} attributes={{ target: "_blank" }} fullWidth={true} className={"hover:bg-neutral-faded"}>
                    <EntryTile entry={item.entry} distance={item.distance}/>
                </Actionable>
            </li>);
        }
    );

    return hitlist.numberOfHits > 0 ? (
        <ScrollArea scrollbarDisplay="visible" className={"print:!overflow-visible print:*:!overflow-visible"}>
            <View paddingInline={4}>
                <Text variant="body-3" color="neutral-faded" weight="regular">
                    {t("search_sidebar.search_result_list.hit_count", { count: hitlist.numberOfHits })}
                </Text>
            </View>
            <ul className="list-none">
                {hits}
            </ul>
        </ScrollArea>
    ) : <SearchResultEmpty/>;
}

function SearchResultEmpty() {
    const { t } = useTranslation();

    return (
        <>
            <View paddingInline={4}>
                <Text variant="body-3" color="neutral-faded" weight="regular">
                    {t("search_sidebar.search_result_list.no_hits")}
                </Text>
            </View>
            <View paddingTop={10} paddingInline={25} justify={"center"}>
                <img src={RAILS_ASSET_URL("/web_search/search_empty_state.svg")} alt="" aria-hidden="true" />
            </View>
        </>
    );
}

function SearchResultListLoading() {
    return (
        <ScrollArea scrollbarDisplay="visible">
            <View paddingInline={4}>
                <Text variant="body-3" color="neutral-faded" weight="regular">
                    <Skeleton width={"165px"} height={5}/>
                </Text>
            </View>
            <EntryTileLoading/>
            <EntryTileLoading/>
            <EntryTileLoading/>
            <EntryTileLoading/>
        </ScrollArea>
    );
}


function ErrorMessage() {
    const { t } = useTranslation();

    return (
        <View padding={4}>
            <Alert title={t("search_sidebar.error_message.title")} icon={AlertTriangleIcon} color="critical">
                {t("search_sidebar.error_message.body")}
            </Alert>
        </View>
    );
}
